import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { dashboardAPI, cvAPI, jobsAPI, gamificationAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import CVPreviewModal from '../components/CVPreviewModal';
import WhatsAppShareModal from '../components/WhatsAppShareModal';
import WhatsAppShareButton from '../components/WhatsAppShareButton';
import LanguageSelector from '../components/LanguageSelector';
import ReferralCard from '../components/ReferralCard';
import UpgradeModal from '../components/UpgradeModal';
import { CardSkeleton, CVCardSkeleton, DashboardStatsSkeleton } from '../components/ui/skeleton-loaders';
import { 
  FileText, Plus, LayoutDashboard, Target, MessageSquare, 
  Settings, LogOut, Briefcase, ChevronRight, Download, 
  Trash2, Edit, AlertCircle, Crown, Menu, X, Eye, 
  GraduationCap, User, Shield, Share2, Sparkles, Award,
  FileSignature, Search, Star, Lightbulb, Mic, Bell,
  DollarSign, Calendar, Mail, TrendingUp, Check, Loader2,
  Building2, Globe, HelpCircle, ArrowUp, FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, isSubscribed } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [cvs, setCvs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [previewCV, setPreviewCV] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [whatsappCV, setWhatsappCV] = useState(null);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [gamification, setGamification] = useState(null);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadCV, setDownloadCV] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('classic');
  const [downloading, setDownloading] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeTrigger, setUpgradeTrigger] = useState('default');
  
  const isAdmin = user?.email === 'admin@careerstep.co.za' || user?.is_admin;

  const templateStyles = [
    { value: 'classic', label: 'Classic', desc: 'Clean and professional', color: '#1e293b' },
    { value: 'modern', label: 'Modern', desc: 'Contemporary with color accents', color: '#0ea5e9' },
    { value: 'executive', label: 'Executive', desc: 'Sophisticated for senior roles', color: '#059669' },
    { value: 'creative', label: 'Creative', desc: 'Bold and distinctive', color: '#8b5cf6' }
  ];

  // All templates are ATS-friendly

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, cvsRes, jobsRes, gamRes] = await Promise.all([
        dashboardAPI.getStats(),
        cvAPI.list(),
        jobsAPI.list(),
        gamificationAPI.getProgress().catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setCvs(cvsRes.data);
      setJobs(jobsRes.data);
      if (gamRes.data) setGamification(gamRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCV = async (cvId) => {
    if (!window.confirm('Are you sure? This CV will be permanently deleted.')) return;
    try {
      await cvAPI.delete(cvId);
      setCvs(cvs.filter(cv => cv.id !== cvId));
      toast.success('CV deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete CV');
    }
  };

  const openDownloadModal = (cv) => {
    setDownloadCV(cv);
    setSelectedTemplate(cv.template_style || 'classic');
    setDownloadModalOpen(true);
  };

  const handleDownloadCV = async (cv, template = null) => {
    setDownloading(true);
    try {
      const response = await cvAPI.download(cv.id, template || selectedTemplate);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${cv.personal_details?.full_name?.replace(' ', '_') || 'CV'}_CV_${template || selectedTemplate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CV downloaded!');
      setDownloadModalOpen(false);

      // Show upgrade modal to free users after download
      if (!isSubscribed) {
        setTimeout(() => {
          setUpgradeTrigger('watermark');
          setUpgradeOpen(true);
        }, 1500);
      }
    } catch (error) {
      toast.error('Failed to download CV');
    } finally {
      setDownloading(false);
    }
  };

  const navItems = [
    { icon: <LayoutDashboard className="w-5 h-5" />, label: t('dashboard.sidebar.dashboard'), path: '/dashboard', active: true },
    { icon: <FileText className="w-5 h-5" />, label: t('dashboard.sidebar.cvBuilder'), path: '/cv-builder', active: false },
    { icon: <Briefcase className="w-5 h-5" />, label: t('dashboard.sidebar.jobTracker'), path: '/job-pipeline', active: false },
    { icon: <Mic className="w-5 h-5" />, label: t('dashboard.sidebar.interview'), path: '/mock-interview', active: false },
    { icon: <GraduationCap className="w-5 h-5" />, label: 'Learnerships', path: '/learnerships', active: false },
    { icon: <Globe className="w-5 h-5" />, label: 'SA Job Boards', path: '/sa-job-boards', active: false },
    { icon: <GraduationCap className="w-5 h-5" />, label: 'Free Training', path: '/training', active: false },
    { icon: <Settings className="w-5 h-5" />, label: t('dashboard.sidebar.settings'), path: '/settings', active: false },
    ...(isAdmin ? [{ icon: <Shield className="w-5 h-5" />, label: t('dashboard.sidebar.admin'), path: '/admin', active: false }] : []),
  ];

  const getATSColor = (score) => {
    if (score >= 80) return 'text-emerald-600 bg-emerald-100';
    if (score >= 50) return 'text-amber-600 bg-amber-100';
    return 'text-red-600 bg-red-100';
  };

  // Get personalized ATS improvement tips based on CV data
  const getATSTips = (cv) => {
    const tips = [];
    const pd = cv.personal_details || {};
    
    // Check what's missing
    if (!cv.summary || cv.summary.length < 50) {
      tips.push({ text: 'Add a professional summary (50+ words)', points: '+10%', priority: 'high' });
    }
    if (!cv.work_experience || cv.work_experience.length === 0) {
      tips.push({ text: 'Add work experience with bullet points', points: '+15%', priority: 'high' });
    } else if (cv.work_experience.some(exp => !exp.responsibilities || exp.responsibilities.length < 3)) {
      tips.push({ text: 'Add 3+ bullet points per job role', points: '+5%', priority: 'medium' });
    }
    if (!cv.education || cv.education.length === 0) {
      tips.push({ text: 'Add your education history', points: '+10%', priority: 'high' });
    }
    if (!cv.skills || cv.skills.length < 5) {
      tips.push({ text: 'Add at least 5 relevant skills', points: '+10%', priority: 'high' });
    }
    if (!cv.languages || cv.languages.length === 0) {
      tips.push({ text: 'Add languages you speak', points: '+5%', priority: 'medium' });
    }
    if (!pd.linkedin) {
      tips.push({ text: 'Add your LinkedIn profile URL', points: '+3%', priority: 'low' });
    }
    if (!pd.drivers_license || pd.drivers_license === 'none') {
      tips.push({ text: 'Add driver\'s license if you have one', points: '+2%', priority: 'low' });
    }
    
    // If score is already high, give maintenance tips
    if (tips.length === 0) {
      tips.push({ text: 'Your CV is well optimized!', points: '', priority: 'success' });
      tips.push({ text: 'Use Job Match to tailor for specific roles', points: '+ATS', priority: 'tip' });
    }
    
    return tips;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-amber-600 bg-amber-50';
      case 'low': return 'text-blue-600 bg-blue-50';
      case 'success': return 'text-emerald-600 bg-emerald-50';
      case 'tip': return 'text-purple-600 bg-purple-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Mobile Header Skeleton */}
        <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 bg-slate-200 rounded animate-pulse"></div>
            <div className="h-6 w-24 bg-slate-200 rounded animate-pulse"></div>
          </div>
          <div className="w-10 h-10 bg-slate-200 rounded animate-pulse"></div>
        </header>

        {/* Desktop Sidebar Skeleton */}
        <aside className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 w-64 bg-slate-900 p-6">
          <div className="h-9 w-32 bg-slate-700 rounded animate-pulse mb-8"></div>
          <div className="space-y-2">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-12 bg-slate-800 rounded-lg animate-pulse"></div>
            ))}
          </div>
        </aside>

        {/* Main Content Skeleton */}
        <main className="lg:ml-64 pt-20 lg:pt-8 pb-24 lg:pb-8 px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {/* Welcome Header Skeleton */}
            <div className="mb-8">
              <div className="h-8 w-64 bg-slate-200 rounded animate-pulse mb-2"></div>
              <div className="h-4 w-48 bg-slate-200 rounded animate-pulse"></div>
            </div>

            {/* Stats Grid Skeleton */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[1,2,3,4].map(i => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="h-4 w-20 bg-slate-200 rounded animate-pulse mb-2"></div>
                  <div className="h-8 w-16 bg-slate-200 rounded animate-pulse"></div>
                </div>
              ))}
            </div>

            {/* CVs Section Skeleton */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="h-6 w-24 bg-slate-200 rounded animate-pulse"></div>
                <div className="h-10 w-32 bg-slate-200 rounded animate-pulse"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1,2].map(i => (
                  <CVCardSkeleton key={i} />
                ))}
              </div>
            </div>

            {/* Quick Actions Skeleton */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1,2,3,4].map(i => (
                <CardSkeleton key={i} />
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src="/logo-nav.png" alt="Career Step" className="h-9 w-auto" />
          <span className="font-bold text-lg text-slate-900">Career Step</span>
        </Link>
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 text-slate-600"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-white pt-16">
          <nav className="p-4 space-y-2">
            {navItems.map((item, index) => (
              <Link
                key={index}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  item.active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {item.icon}
                {item.label}
              </Link>
            ))}
            <hr className="my-4" />
            <button 
              onClick={() => { logout(); navigate('/'); }}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 w-full"
            >
              <LogOut className="w-5 h-5" />
              {t('dashboard.sidebar.logout')}
            </button>
          </nav>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 w-64 bg-slate-900 text-white p-6">
        <Link to="/dashboard" className="flex items-center gap-2 mb-8">
          <img src="/logo-nav.png" alt="Career Step" className="h-9 w-auto" />
          <span className="font-bold text-xl">Career Step</span>
        </Link>

        <nav className="flex-1 space-y-1">
          {navItems.map((item, index) => (
            <Link
              key={index}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                item.active ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {item.icon}
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="border-t border-slate-700 pt-4 mt-4">
          {/* Subscription Status */}
          {isSubscribed ? (
            <div className="px-4 py-2 mb-2 bg-emerald-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-emerald-400">
                <Crown className="w-4 h-4" />
                <span className="text-sm font-medium">{t('dashboard.subscription.active')}</span>
              </div>
            </div>
          ) : (
            <Link 
              to="/subscribe"
              className="flex items-center gap-3 px-4 py-3 mb-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors"
            >
              <Crown className="w-5 h-5" />
              {t('dashboard.subscription.subscribe')} - R25
            </Link>
          )}
          
          <div className="px-4 py-2">
            <p className="text-sm text-slate-400">Signed in as</p>
            <p className="font-medium truncate">{user?.full_name}</p>
          </div>
          <button 
            onClick={() => { logout(); navigate('/'); }}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 w-full"
          >
            <LogOut className="w-5 h-5" />
            {t('dashboard.sidebar.logout')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-20 lg:pt-8 pb-24 lg:pb-8 px-4 lg:px-8 overflow-x-hidden">
        <div className="max-w-6xl mx-auto">
          {/* Welcome Header */}
          <div className="mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('dashboard.welcome')}, {user?.full_name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-600">Here's your career progress overview</p>
          </div>

          {/* Upgrade Banner - show for free users */}
          {!isSubscribed && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Crown className="w-6 h-6 text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-800">Upgrade for Watermark-Free PDFs</p>
                  <p className="text-amber-700 text-sm">Free tier: 1 CV with watermark. Upgrade for 6 CVs + clean PDFs!</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/subscribe')}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                Upgrade - R25
              </Button>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-slate-500 text-sm mb-1">{t('dashboard.stats.cvs')}</p>
              <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {stats?.cv_count || 0}<span className="text-lg text-slate-400">/{stats?.max_cvs || 5}</span>
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-slate-500 text-sm mb-1">{t('dashboard.stats.atsScore')}</p>
              <p className={`text-3xl font-bold ${stats?.average_ats_score >= 70 ? 'text-emerald-600' : 'text-amber-600'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {stats?.average_ats_score || 0}%
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-slate-500 text-sm mb-1">Completeness</p>
              <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {stats?.average_completeness || 0}%
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-slate-500 text-sm mb-1">{t('dashboard.stats.applications')}</p>
              <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {stats?.jobs_tracked || 0}
              </p>
            </div>
          </div>

          {/* My CVs Section */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('dashboard.myCvs')}</h2>
              {(stats?.cv_count || 0) < (stats?.max_cvs || 1) ? (
                <Button 
                  data-testid="create-cv-btn"
                  onClick={() => navigate('/cv-builder')}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('dashboard.createCv')}
                </Button>
              ) : !isSubscribed ? (
                <Button 
                  data-testid="create-cv-upgrade-btn"
                  onClick={() => { setUpgradeTrigger('cv_limit'); setUpgradeOpen(true); }}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Create More CVs
                </Button>
              ) : null}
            </div>

            {cvs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">{t('dashboard.noCvs')}</p>
                <Button 
                  onClick={() => navigate('/cv-builder')}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  {t('dashboard.createCv')}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {cvs.map((cv) => (
                  <div 
                    key={cv.id}
                    data-testid={`cv-card-${cv.id}`}
                    className="p-4 border border-slate-200 rounded-xl hover:border-slate-300 transition-colors"
                  >
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-6 h-6 text-slate-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-slate-900 truncate">
                          {cv.title || cv.personal_details?.full_name || 'Untitled CV'}
                        </h3>
                        <p className="text-sm text-slate-500">
                          Updated {new Date(cv.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button 
                            className={`px-3 py-1 rounded-full text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1 flex-shrink-0 ${getATSColor(cv.ats_score)}`}
                            title="Click to see how to improve your score"
                          >
                            ATS: {cv.ats_score}%
                            <HelpCircle className="w-3 h-3 opacity-60" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-0" align="end">
                          <div className="p-4 border-b border-slate-100">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                                <ArrowUp className="w-4 h-4 text-emerald-500" />
                                Improve Your ATS Score
                              </h4>
                              <span className={`px-2 py-0.5 rounded text-xs font-bold ${getATSColor(cv.ats_score)}`}>
                                {cv.ats_score}%
                              </span>
                            </div>
                            <p className="text-xs text-slate-500">
                              Higher scores = better chances of passing employer filters
                            </p>
                          </div>
                          <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
                            {getATSTips(cv).map((tip, idx) => (
                              <div key={idx} className={`p-2 rounded-lg flex items-start gap-2 ${getPriorityColor(tip.priority)}`}>
                                {tip.priority === 'success' ? (
                                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                ) : tip.priority === 'tip' ? (
                                  <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                ) : (
                                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                )}
                                <div className="flex-1">
                                  <p className="text-sm font-medium">{tip.text}</p>
                                  {tip.points && (
                                    <span className="text-xs opacity-75">{tip.points}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="p-3 border-t border-slate-100 bg-slate-50">
                            <Button 
                              size="sm" 
                              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                              onClick={() => navigate(`/cv-builder/${cv.id}`)}
                            >
                              <Edit className="w-3 h-3 mr-2" />
                              Edit CV to Improve Score
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          data-testid={`whatsapp-cv-${cv.id}`}
                          onClick={() => { setWhatsappCV(cv); setWhatsappOpen(true); }}
                          title="Share via WhatsApp"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          <Share2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          data-testid={`preview-cv-${cv.id}`}
                          onClick={() => { setPreviewCV(cv); setPreviewOpen(true); }}
                          title="Preview CV"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          data-testid={`edit-cv-${cv.id}`}
                          onClick={() => navigate(`/cv-builder/${cv.id}`)}
                          title="Edit CV"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          data-testid={`download-cv-${cv.id}`}
                          onClick={() => openDownloadModal(cv)}
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <WhatsAppShareButton 
                          cvTitle={cv.title || 'My CV'}
                          cvId={cv.id}
                          variant="icon"
                        />
                        <Button 
                          variant="ghost" 
                          size="icon"
                          data-testid={`delete-cv-${cv.id}`}
                          onClick={() => handleDeleteCV(cv.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          title="Delete CV"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Gamification Section */}
          {gamification && (
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-6 mb-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Award className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-amber-100 text-sm">Your Progress</p>
                    <p className="text-xl font-bold">{gamification.level} • {gamification.overall_progress}%</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-amber-100 text-sm">Badges Earned</p>
                  <p className="text-xl font-bold">{gamification.badges.filter(b => b.earned).length}/{gamification.badges.length}</p>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="bg-white/20 rounded-full h-3 mb-4">
                <div 
                  className="bg-white rounded-full h-3 transition-all duration-500"
                  style={{ width: `${gamification.overall_progress}%` }}
                />
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                {gamification.badges.map((badge) => (
                  <div
                    key={badge.id}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                      badge.earned ? 'bg-white/30' : 'bg-white/10 opacity-60'
                    }`}
                    title={badge.description}
                  >
                    <span>{badge.icon}</span>
                    <span className="font-medium">{badge.name}</span>
                  </div>
                ))}
              </div>

              {/* Daily Tip */}
              <div className="bg-white/10 rounded-xl p-3 flex items-start gap-2">
                <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm">{gamification.daily_tip}</p>
              </div>
            </div>
          )}

          {/* Quick Actions - Core Features */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <button
              onClick={() => navigate('/cv-builder')}
              className="bg-white rounded-xl border border-slate-200 p-6 text-left hover:border-slate-900 hover:shadow-md transition-all group"
            >
              <FileText className="w-8 h-8 text-slate-900 mb-3" />
              <h3 className="font-semibold text-slate-900 mb-1">{t('dashboard.sidebar.cvBuilder')}</h3>
              <p className="text-sm text-slate-500">Create ATS-friendly CVs</p>
              <ChevronRight className="w-5 h-5 text-slate-400 mt-3 group-hover:translate-x-1 transition-transform" />
            </button>

            <button
              onClick={() => navigate('/job-pipeline')}
              className="bg-white rounded-xl border border-slate-200 p-6 text-left hover:border-blue-500 hover:shadow-md transition-all group"
            >
              <Briefcase className="w-8 h-8 text-blue-500 mb-3" />
              <h3 className="font-semibold text-slate-900 mb-1">{t('dashboard.sidebar.jobTracker')}</h3>
              <p className="text-sm text-slate-500">{t('dashboard.tools.trackerDesc')}</p>
              <ChevronRight className="w-5 h-5 text-slate-400 mt-3 group-hover:translate-x-1 transition-transform" />
            </button>

            <button
              onClick={() => navigate('/mock-interview')}
              className="bg-white rounded-xl border border-slate-200 p-6 text-left hover:border-purple-500 hover:shadow-md transition-all group"
            >
              <Mic className="w-8 h-8 text-purple-500 mb-3" />
              <h3 className="font-semibold text-slate-900 mb-1">{t('dashboard.sidebar.interview')}</h3>
              <p className="text-sm text-slate-500">{t('dashboard.tools.interviewDesc')}</p>
              <ChevronRight className="w-5 h-5 text-slate-400 mt-3 group-hover:translate-x-1 transition-transform" />
            </button>

            <button
              onClick={() => navigate('/cover-letter')}
              className="bg-white rounded-xl border border-slate-200 p-6 text-left hover:border-violet-500 hover:shadow-md transition-all group"
            >
              <FileSignature className="w-8 h-8 text-violet-500 mb-3" />
              <h3 className="font-semibold text-slate-900 mb-1">{t('dashboard.tools.coverLetter')}</h3>
              <p className="text-sm text-slate-500">{t('dashboard.tools.coverLetterDesc')}</p>
              <ChevronRight className="w-5 h-5 text-slate-400 mt-3 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Career Tools */}
          <div className="mb-8">
            <h2 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('dashboard.tools')}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => navigate('/job-match')}
                data-testid="job-match-card"
                className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-emerald-500 hover:shadow-md transition-all group"
              >
                <Target className="w-7 h-7 text-emerald-500 mb-2" />
                <h3 className="font-semibold text-slate-900 text-sm">Job Match</h3>
                <p className="text-xs text-slate-500 mt-1">Analyze job fit & skill gaps</p>
              </button>

              <button
                onClick={() => navigate('/cv-enhancer')}
                data-testid="cv-enhancer-card"
                className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-amber-500 hover:shadow-md transition-all group"
              >
                <Sparkles className="w-7 h-7 text-amber-500 mb-2" />
                <h3 className="font-semibold text-slate-900 text-sm">CV Enhancer</h3>
                <p className="text-xs text-slate-500 mt-1">Score & improve your CV</p>
              </button>

              <button
                onClick={() => navigate('/cv-review')}
                data-testid="cv-review-card"
                className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-violet-500 hover:shadow-md transition-all group"
              >
                <Star className="w-7 h-7 text-violet-500 mb-2" />
                <h3 className="font-semibold text-slate-900 text-sm">AI CV Review</h3>
                <p className="text-xs text-slate-500 mt-1">Expert AI feedback & tips</p>
              </button>

              <button
                onClick={() => navigate('/salary-coach')}
                data-testid="salary-coach-card"
                className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-emerald-500 hover:shadow-md transition-all group"
              >
                <DollarSign className="w-7 h-7 text-emerald-500 mb-2" />
                <h3 className="font-semibold text-slate-900 text-sm">Salary Coach</h3>
                <p className="text-xs text-slate-500 mt-1">Negotiate your worth</p>
              </button>

              <button
                onClick={() => navigate('/career-path')}
                data-testid="career-path-card"
                className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-indigo-500 hover:shadow-md transition-all group"
              >
                <TrendingUp className="w-7 h-7 text-indigo-500 mb-2" />
                <h3 className="font-semibold text-slate-900 text-sm">Career Planner</h3>
                <p className="text-xs text-slate-500 mt-1">Map your career path</p>
              </button>

              <button
                onClick={() => navigate('/documents')}
                data-testid="documents-card"
                className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-sky-500 hover:shadow-md transition-all group"
              >
                <Mail className="w-7 h-7 text-sky-500 mb-2" />
                <h3 className="font-semibold text-slate-900 text-sm">Documents</h3>
                <p className="text-xs text-slate-500 mt-1">Letters & email templates</p>
              </button>

              <button
                onClick={() => navigate('/first-90-days')}
                data-testid="first-90-days-card"
                className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-amber-500 hover:shadow-md transition-all group"
              >
                <Calendar className="w-7 h-7 text-amber-500 mb-2" />
                <h3 className="font-semibold text-slate-900 text-sm">First 90 Days</h3>
                <p className="text-xs text-slate-500 mt-1">New job success guide</p>
              </button>

              <button
                onClick={() => navigate('/workplace-rights')}
                data-testid="workplace-rights-card"
                className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-rose-500 hover:shadow-md transition-all group"
              >
                <Shield className="w-7 h-7 text-rose-500 mb-2" />
                <h3 className="font-semibold text-slate-900 text-sm">Workplace Rights</h3>
                <p className="text-xs text-slate-500 mt-1">Know your SA rights</p>
              </button>

              <button
                onClick={() => navigate('/community')}
                data-testid="community-card"
                className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-emerald-500 hover:shadow-md transition-all group"
              >
                <Award className="w-7 h-7 text-emerald-500 mb-2" />
                <h3 className="font-semibold text-slate-900 text-sm">Community</h3>
                <p className="text-xs text-slate-500 mt-1">Success stories & tips</p>
              </button>

              <button
                onClick={() => navigate('/supporting-documents')}
                data-testid="supporting-docs-card"
                className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-teal-500 hover:shadow-md transition-all group"
              >
                <FolderOpen className="w-7 h-7 text-teal-500 mb-2" />
                <h3 className="font-semibold text-slate-900 text-sm">Supporting Docs</h3>
                <p className="text-xs text-slate-500 mt-1">Certificates & diplomas</p>
              </button>

              <button
                onClick={() => navigate('/training')}
                data-testid="free-training-card"
                className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-violet-500 hover:shadow-md transition-all group"
              >
                <GraduationCap className="w-7 h-7 text-violet-500 mb-2" />
                <h3 className="font-semibold text-slate-900 text-sm">Free Training</h3>
                <p className="text-xs text-slate-500 mt-1">Free courses & certificates</p>
              </button>
            </div>
          </div>

          {/* SA-Specific Features Section */}
          <div className="mt-8">
            <h2 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              🇿🇦 South Africa Features
            </h2>
            <p className="text-sm text-slate-500 mb-4">Tools designed specifically for the SA job market</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <button
                onClick={() => navigate('/gov-jobs')}
                data-testid="gov-jobs-card"
                className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200 p-4 text-left hover:shadow-md transition-all group"
              >
                <Building2 className="w-7 h-7 text-emerald-600 mb-2" />
                <h3 className="font-semibold text-emerald-900 text-sm">Government Jobs</h3>
                <p className="text-xs text-emerald-700 mt-1">Z83 form helper & guide</p>
              </button>

              <button
                onClick={() => navigate('/learnerships')}
                data-testid="learnerships-card"
                className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200 p-4 text-left hover:shadow-md transition-all group"
              >
                <GraduationCap className="w-7 h-7 text-amber-600 mb-2" />
                <h3 className="font-semibold text-amber-900 text-sm">Learnerships</h3>
                <p className="text-xs text-amber-700 mt-1">SETA programmes finder</p>
              </button>

              <button
                onClick={() => navigate('/sa-job-boards')}
                data-testid="sa-job-boards-card"
                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200 p-4 text-left hover:shadow-md transition-all group"
              >
                <Globe className="w-7 h-7 text-blue-600 mb-2" />
                <h3 className="font-semibold text-blue-900 text-sm">SA Job Boards</h3>
                <p className="text-xs text-blue-700 mt-1">Careers24, PNet & more</p>
              </button>
            </div>
          </div>

          {/* Referral Program */}
          <div className="mt-8">
            <h2 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Invite Friends
            </h2>
            <div className="max-w-md">
              <ReferralCard />
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-2 py-2 flex justify-around items-center safe-area-pb z-50">
        {navItems.slice(0, 4).map((item, index) => (
          <Link
            key={index}
            to={item.path}
            className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg ${
              item.active ? 'text-slate-900' : 'text-slate-400'
            }`}
          >
            {item.icon}
            <span className="text-xs">{item.label.split(' ')[0]}</span>
          </Link>
        ))}
      </nav>

      {/* CV Preview Modal */}
      <CVPreviewModal
        cv={previewCV}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onDownload={(template) => previewCV && handleDownloadCV(previewCV, template)}
      />

      {/* WhatsApp Share Modal */}
      <WhatsAppShareModal
        isOpen={whatsappOpen}
        onClose={() => setWhatsappOpen(false)}
        cv={whatsappCV}
      />

      {/* Download Template Selection Modal */}
      {downloadModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={() => setDownloadModalOpen(false)} 
          />
          <div className="relative bg-white rounded-2xl p-6 max-w-lg w-full mx-4 shadow-xl">
            <button 
              onClick={() => setDownloadModalOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h3 className="text-xl font-bold text-slate-900 mb-2">Download CV</h3>
            <p className="text-slate-500 mb-6">Choose a template design for your PDF</p>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              {templateStyles.map((style) => (
                <button
                  key={style.value}
                  onClick={() => setSelectedTemplate(style.value)}
                  data-testid={`download-template-${style.value}`}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    selectedTemplate === style.value 
                      ? 'bg-slate-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  style={{ borderColor: selectedTemplate === style.value ? style.color : undefined }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: style.color }}
                    />
                    <span className="font-semibold text-slate-900">{style.label}</span>
                    {selectedTemplate === style.value && (
                      <Check className="w-4 h-4 ml-auto" style={{ color: style.color }} />
                    )}
                  </div>
                  <p className="text-xs text-slate-500">{style.desc}</p>
                </button>
              ))}
            </div>
            
            <Button
              onClick={() => downloadCV && handleDownloadCV(downloadCV, selectedTemplate)}
              disabled={downloading}
              className="w-full bg-slate-900 hover:bg-slate-800"
              data-testid="confirm-download-btn"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating PDF...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download {selectedTemplate.charAt(0).toUpperCase() + selectedTemplate.slice(1)} Template
                </>
              )}
            </Button>
            
            <p className="text-xs text-amber-600 mt-3 text-center flex items-center justify-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Review your CV before interviews - know your content so you can confidently answer any questions.
            </p>
          </div>
        </div>
      )}

      {/* Upgrade Modal for free users */}
      <UpgradeModal
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        trigger={upgradeTrigger}
      />
    </div>
  );
};

export default DashboardPage;
